import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowDown } = FiIcons;

const HeroSection = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="pt-32 pb-20 gradient-bg-light">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <h1 className="font-playfair text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Peak Performance{' '}
                <span className="gradient-text">Starts Here.</span>
              </h1>
              
              <p className="text-xl text-gray-700 leading-relaxed">
                The real reason you're not dominating? Your brain needs a competitive edge. We help you train it for peak performance, with neuroscience-backed cognitive therapy that upgrades your mental game, not just your physical training.{' '}
                <span className="font-semibold text-accent-magenta">Trusted by Olympic Gold Medalists.</span>
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-gray-100">
              <p className="text-lg text-gray-800 mb-4 font-medium">
                Crushing it in practice but choking in competition?
              </p>
              <p className="text-gray-700 leading-relaxed">
                Here's the thing: It's not just about physical training. It's about the brain skills that control split-second decisions, focus under pressure, and clutch performance—attention, working memory, and impulse control.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                <span className="font-semibold">Elite athletes leverage their executive functions more efficiently than lower-level players</span>, creating the mental edge that separates the champions from the players.
              </p>
              <p className="text-accent-magenta font-semibold mt-4">
                We sharpen that edge. For good.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollToSection('contact')}
                className="btn-primary text-lg"
              >
                Book Your Game-Changer Call
              </button>
              
              <button
                onClick={() => scrollToSection('problem')}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                Learn More
                <SafeIcon icon={FiArrowDown} className="h-5 w-5" />
              </button>
            </div>
          </motion.div>

          {/* Right Content - Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative z-10">
              <img
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Professional athlete in focused training - representing peak mental performance"
                className="w-full h-96 lg:h-[500px] object-cover rounded-2xl shadow-2xl"
              />
              
              {/* Floating stats cards */}
              <div className="absolute -top-6 -left-6 bg-white p-4 rounded-xl shadow-lg border border-gray-100">
                <div className="text-2xl font-bold text-accent-magenta">28%</div>
                <div className="text-sm text-gray-600">Faster Decisions</div>
              </div>
              
              <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-lg border border-gray-100">
                <div className="text-2xl font-bold text-accent-gold">15%</div>
                <div className="text-sm text-gray-600">Fewer Errors</div>
              </div>
            </div>
            
            {/* Background decorative elements */}
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-accent-light rounded-full opacity-50 -z-10"></div>
            <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-accent-purple rounded-full opacity-50 -z-10"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;